import moment, { Moment } from "moment";

class DateUtils {
    static DEFAULT_INTERVAL = 15;

    static getNextMinuteInterval(
        value: Date | Moment | null | undefined = new Date(),
        step: number = 15
    ) {
        const date = value || new Date();
        const sanitizedDate = moment(date).seconds(0).milliseconds(0).toDate();
        const nMinutesBeforeNextStep = moment(sanitizedDate).minute() % step;
        const reminder = nMinutesBeforeNextStep
            ? step - nMinutesBeforeNextStep
            : nMinutesBeforeNextStep;

        return moment(sanitizedDate)
            .add(reminder, "minutes")
            .seconds(0)
            .milliseconds(0);
    }

    static get defaultStartTime() {
        return DateUtils.getNextMinuteInterval().toDate();
    }

    static get defaultEndTime() {
        return DateUtils.getNextMinuteInterval().add(1, "hour").toDate();
    }

    static getStartEnd(date: Date, hourInterval: number = 1) {
        const start = DateUtils.getNextMinuteInterval(date).toDate();
        const end = moment(start).add(hourInterval, "hour").toDate();

        return { start, end };
    }

    static greaterDay(date1: Date, date2: Date) {
        const ticks1 = moment(date1).startOf("day").toDate().getTime();
        const ticks2 = moment(date2).startOf("day").toDate().getTime();

        return ticks1 > ticks2;
    }

    static getAge(dateOfBirth: Date) {
        const currentYear = new Date().getFullYear();
        const year = dateOfBirth.getFullYear();

        return currentYear - year;
    }

    static roundMinutes(m: Moment) {
        return m.second() < 30
            ? DateUtils.roundDownMinutes(m)
            : DateUtils.roundUp(m);
    }

    static roundDownMinutes(m: Moment): moment.Moment {
        return m.startOf("minute");
    }

    static roundUp(m: Moment): Moment {
        return m.second() || m.millisecond()
            ? m.add(1, "minute").startOf("minute")
            : m.startOf("minute");
    }

    static displayMinutes(minutes: number): string {
        const roundMinutes = Math.round(minutes);
        const nMinutes = roundMinutes % 60;
        const minuteFormat = nMinutes < 10 ? 0 : "";
        const nHours = Math.floor(roundMinutes / 60);
        const hourFormat = nHours < 10 ? 0 : "";

        return `${hourFormat}${nHours}h ${minuteFormat}${nMinutes}min`;
    }

    static ticksToDate = (ticks: number) => {
        const ticksInMilisecond = 10000;
        const currentDate = moment()
            .set({ hours: 0, minutes: 0, seconds: 0, millisecond: 0 })
            .toDate();
        return new Date(currentDate.getTime() + ticks / ticksInMilisecond);
    };

    static formatDate = (dateString: Date) => {
        return moment(dateString).format("LL");
    };

    static formatTime = (dateString: Date) => {
        return moment(dateString).format("HH[h]mm");
    };

    static formatInOutHours = (dateString: Date, endDate: Date | string) => {
        if (typeof endDate === "string") {
            return `${moment(dateString).format("HH[h]mm")} ➝ ${endDate}`;
        }
        return `${moment(dateString).format("HH[h]mm")} ➝ ${moment(
            endDate
        ).format("HH[h]mm")}`;
    };

    static convertMinutesToHours = (minutes: number) =>
        Math.round(minutes / 60);

    static isToday = (date: Date) => moment(date).isSame(moment(), "day");

    static isSameDateTime = (
        firstDate: Date | undefined,
        secondDate: Date | undefined,
    ): boolean => {
        if (!firstDate && !secondDate) return true;
        if (!firstDate || !secondDate) return false;
        return moment(firstDate).isSame(moment(secondDate), "minute");
    };
}

export default DateUtils;
